'use client';

import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@/lib/auth-client';
import { unwrapLambdaResponse } from '@/lib/api';
import { BACKEND_URL } from '@/lib/constants';

export type SpaceKind = 'standard' | 'organization' | 'demo';
export type SpaceRole = 'admin' | 'editor' | 'viewer';

export interface Space {
  id: string;
  name: string;
  kind: SpaceKind;
  status: 'active' | 'archived';
  currentUserRole: SpaceRole;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
  _count?: { members: number };
}

export async function fetchSpaces(
  getAccessToken: () => Promise<string | undefined>
): Promise<Space[]> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BACKEND_URL}/spaces`, {
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load spaces');
  return unwrapLambdaResponse<{ spaces: Space[] }>(await res.json()).spaces;
}

export function useSpaces() {
  const { getAccessToken, loading: authLoading } = useAccessToken();
  return useQuery({
    queryKey: ['spaces'],
    queryFn: () => fetchSpaces(getAccessToken),
    enabled: !authLoading,
  });
}

export function canWriteSpace(space: Space | undefined): boolean {
  return Boolean(space && space.currentUserRole !== 'viewer');
}
